/**
 * TanStack Query hooks for `creative-ab`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	assign,
	convert,
	creativeAbQueryKeys,
	listVariants,
	replaceVariants,
	report,
	type AssignResponse,
	type ConvertResponse,
	type ReportResponse,
	type VariantInput,
	type VariantsResponse,
} from "./api";

export function useCreativeAbVariants(
	campaignId: string,
): UseQueryResult<VariantsResponse, Error> {
	return useQuery({
		queryKey: creativeAbQueryKeys.variants(campaignId),
		queryFn: () => listVariants(campaignId),
		enabled: campaignId.length > 0,
	});
}

export function useCreativeAbReport(
	campaignId: string,
): UseQueryResult<ReportResponse, Error> {
	return useQuery({
		queryKey: creativeAbQueryKeys.report(campaignId),
		queryFn: () => report(campaignId),
		enabled: campaignId.length > 0,
	});
}

export function useCreativeAbReplaceVariants(
	campaignId: string,
): UseMutationResult<VariantsResponse, Error, VariantInput[]> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (variants: VariantInput[]) =>
			replaceVariants(campaignId, variants),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: creativeAbQueryKeys.all });
		},
	});
}

export function useCreativeAbAssign(
	campaignId: string,
): UseMutationResult<AssignResponse, Error, string> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (clickId: string) => assign(campaignId, clickId),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: creativeAbQueryKeys.report(campaignId),
			});
		},
	});
}

export function useCreativeAbConvert(
	campaignId: string,
): UseMutationResult<ConvertResponse, Error, string> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (clickId: string) => convert(campaignId, clickId),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: creativeAbQueryKeys.report(campaignId),
			});
		},
	});
}
